// Change #100463 - Super Admin > Licence Enforcement.
//
// A user can belong to a company without a licence, but once the company is
// enforced they cannot use the system until one is assigned. This lists every
// company with its members who have no live licence (the people who would be
// locked out) and the switch to enforce it, Enterprise included: nothing is
// enforced automatically. Support logins visiting a customer are not listed (they are exempt).

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Badge, Card, CardBody, CardHeader, Col, Input, Row, Table } from "reactstrap";
import { toast } from "react-toastify";

import AppModal from "../../../../Components/Common/AppModal";
import LoadingSpinner from "../../../../Components/Common/Spinner";
import NoDataContent from "../../../../Components/Common/NoDataContent";
import HandleDateTime from "../../../../Components/Common/HandleDateTime";
import {
  licenceEnforcementAdminListApi,
  licenceEnforcementAdminUpdateApi,
} from "../../../../helpers/fakebackend_helper";

export default function LicenceEnforcement() {
  const [rows, setRows] = useState([]);
  const [ready, setReady] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [onlyUnlicensed, setOnlyUnlicensed] = useState(true);
  const [hideTest, setHideTest] = useState(true);
  const [open, setOpen] = useState({});
  const [confirm, setConfirm] = useState(null); // { title, text, rows: [{Company_Id, FlagEnforceUserLicence}] }
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await licenceEnforcementAdminListApi();
      if (res?.status === 200) {
        setRows(res.licenceenforcement_list || []);
        setReady(Boolean(res.ready));
        setError("");
      } else {
        setRows([]);
        setError(res?.message || "Could not load the report");
      }
    } catch (e) {
      setError("Could not load the report");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter(
      (r) =>
        (!onlyUnlicensed || r.Unlicensed > 0) &&
        (!hideTest || String(r.FlagTest) !== "1") &&
        (!q ||
          String(r.BusinessName || "").toLowerCase().includes(q) ||
          (r.unlicensed_list || []).some(
            (u) =>
              String(u.UserFullName || "").toLowerCase().includes(q) ||
              String(u.Email || "").toLowerCase().includes(q)
          ))
    );
  }, [rows, search, onlyUnlicensed, hideTest]);

  const totals = useMemo(
    () => ({
      companies: rows.length,
      enforced: rows.filter((r) => r.Enforced).length,
      unlicensed: rows.reduce((s, r) => s + (r.Enforced ? 0 : r.Unlicensed), 0),
      cleanNotEnforced: rows.filter((r) => !r.Enforced && r.Unlicensed === 0),
      enterpriseNotEnforced: rows.filter((r) => !r.Enforced && r.Mode === "ENTERPRISE"),
    }),
    [rows]
  );

  const ask = (r, on) =>
    setConfirm({
      title: on ? "Enforce licences" : "Stop enforcing licences",
      text: on
        ? r.Unlicensed > 0
          ? `${r.Unlicensed} member${r.Unlicensed === 1 ? "" : "s"} of ${r.BusinessName} will lose access until they are given a licence.`
          : `Every member of ${r.BusinessName} has a licence, so nobody loses access.`
        : `Members of ${r.BusinessName} without a licence will be able to use the system again.`,
      rows: [{ Company_Id: r.Company_Id, FlagEnforceUserLicence: on ? 1 : 0 }],
    });

  const askAllClean = () =>
    setConfirm({
      title: "Enforce where nobody is affected",
      text: `Turns enforcement on for ${totals.cleanNotEnforced.length} compan${
        totals.cleanNotEnforced.length === 1 ? "y" : "ies"
      } where every member already has a licence. Nobody loses access.`,
      rows: totals.cleanNotEnforced.map((r) => ({ Company_Id: r.Company_Id, FlagEnforceUserLicence: 1 })),
    });

  const askEnterprise = () => {
    const list = totals.enterpriseNotEnforced;
    const affected = list.reduce((n, r) => n + r.Unlicensed, 0);
    setConfirm({
      title: "Enforce all Enterprise companies",
      text:
        `Turns enforcement on for ${list.length} Enterprise compan${list.length === 1 ? "y" : "ies"}. ` +
        (affected > 0
          ? `${affected} member${affected === 1 ? "" : "s"} without a seat will lose access until they are given one.`
          : "Every member already has a seat, so nobody loses access."),
      rows: list.map((r) => ({ Company_Id: r.Company_Id, FlagEnforceUserLicence: 1 })),
    });
  };

  const save = async () => {
    if (!confirm?.rows?.length) return;
    setSaving(true);
    try {
      const res = await licenceEnforcementAdminUpdateApi(confirm.rows);
      if (res?.status === 200) {
        toast.success(res.message || "Updated");
        setConfirm(null);
        load();
      } else {
        toast.error(res?.message || "Could not update");
      }
    } catch (e) {
      toast.error("Could not update");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Card>
        <CardHeader className="border-0">
          <Row className="g-2 align-items-end">
            <Col lg={5}>
              <h5 className="mb-1">Licence Enforcement</h5>
              <p className="text-muted mb-0 fs-13">
                Members can be in a company without a licence, but once it is enforced they cannot use the system until
                one is assigned, and only licensed users count towards usage.
              </p>
            </Col>
            <Col lg={3}>
              <div className="search-box">
                <Input
                  type="search"
                  className="form-control search"
                  placeholder="Search company or user..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                <i className="ri-search-line search-icon"></i>
              </div>
            </Col>
            <Col lg={4} className="text-lg-end">
              <div className="d-inline-flex flex-wrap gap-3 align-items-center">
                <div className="form-check form-switch mb-0">
                  <Input
                    type="checkbox"
                    className="form-check-input"
                    id="le-only-unlicensed"
                    checked={onlyUnlicensed}
                    onChange={(e) => setOnlyUnlicensed(e.target.checked)}
                  />
                  <label className="form-check-label fs-13" htmlFor="le-only-unlicensed">
                    Only with unlicensed
                  </label>
                </div>
                <div className="form-check form-switch mb-0">
                  <Input
                    type="checkbox"
                    className="form-check-input"
                    id="le-hide-test"
                    checked={hideTest}
                    onChange={(e) => setHideTest(e.target.checked)}
                  />
                  <label className="form-check-label fs-13" htmlFor="le-hide-test">
                    Hide test
                  </label>
                </div>
              </div>
            </Col>
          </Row>
          {!loading && !error && (
            <div className="d-flex flex-wrap gap-2 align-items-center mt-3">
              <Badge color="light" className="text-body fs-12">
                {totals.companies} companies
              </Badge>
              <Badge color="" className="badge-soft-success fs-12">
                {totals.enforced} enforced
              </Badge>
              <Badge color="" className="badge-soft-warning fs-12">
                {totals.unlicensed} unlicensed users in companies not enforced
              </Badge>
              {ready && totals.enterpriseNotEnforced.length > 0 && (
                <button type="button" className="btn btn-sm btn-soft-primary ms-auto" onClick={askEnterprise}>
                  <i className="ri-shield-star-line me-1"></i>
                  Enforce all Enterprise ({totals.enterpriseNotEnforced.length})
                </button>
              )}
              {ready && totals.cleanNotEnforced.length > 0 && (
                <button
                  type="button"
                  className={`btn btn-sm btn-soft-success ${totals.enterpriseNotEnforced.length > 0 ? "" : "ms-auto"}`}
                  onClick={askAllClean}
                >
                  <i className="ri-shield-check-line me-1"></i>
                  Enforce where nobody is affected ({totals.cleanNotEnforced.length})
                </button>
              )}
            </div>
          )}
          {!loading && !error && !ready && (
            <div className="alert alert-warning fs-13 mt-3 mb-0">
              The report is live, but switching enforcement on needs the database update for change #100463 to run
              first.
            </div>
          )}
        </CardHeader>
        <CardBody className="pt-0">
          {loading ? (
            <LoadingSpinner />
          ) : error ? (
            <div className="alert alert-warning mt-3 mb-0">{error}</div>
          ) : visible.length === 0 ? (
            <NoDataContent
              titleText={rows.length ? "Nothing matches" : "No companies"}
              paragraphText={onlyUnlicensed ? "Every member of every listed company has a licence." : "Try a different search."}
              useButton={false}
            />
          ) : (
            <div className="table-responsive table-card mt-3">
              <Table className="align-middle table-nowrap mb-0">
                <thead className="text-muted">
                  <tr>
                    <th>Company</th>
                    <th>Billing</th>
                    <th className="text-end">Members</th>
                    <th className="text-end">Licensed</th>
                    <th className="text-end">No licence</th>
                    <th>Enforced</th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map((r) => {
                    const isOpen = Boolean(open[r.Company_Id]);
                    return (
                      <React.Fragment key={r.Company_Id}>
                        <tr>
                          <td>
                            <div className="fw-semibold">{r.BusinessName}</div>
                            <div className="text-muted fs-12">
                              #{r.Company_Id}
                              {String(r.FlagTest) === "1" ? " · test" : ""}
                              {r.TrialActive ? " · on trial (not checked until it ends)" : ""}
                            </div>
                          </td>
                          <td>
                            <Badge color={r.Mode === "ENTERPRISE" ? "primary" : "light"} className={r.Mode === "ENTERPRISE" ? "" : "text-muted"}>
                              {r.Mode === "ENTERPRISE" ? "Enterprise" : "Standard"}
                            </Badge>
                          </td>
                          <td className="text-end">{r.Members}</td>
                          <td className="text-end">{r.Licensed}</td>
                          <td className="text-end">
                            {r.Unlicensed > 0 ? (
                              <button
                                type="button"
                                className="btn btn-sm btn-link p-0 fw-bold text-warning"
                                onClick={() => setOpen({ ...open, [r.Company_Id]: !isOpen })}
                              >
                                {r.Unlicensed}
                                <i className={`ms-1 ${isOpen ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}`}></i>
                              </button>
                            ) : (
                              <span className="text-muted">0</span>
                            )}
                          </td>
                          <td>
                            <div className="form-check form-switch mb-0">
                              <Input
                                type="checkbox"
                                className="form-check-input"
                                checked={r.FlagEnforceUserLicence === 1}
                                disabled={!ready}
                                onChange={(e) => ask(r, e.target.checked)}
                              />
                            </div>
                          </td>
                        </tr>
                        {isOpen && (
                          <tr>
                            <td colSpan={6} className="bg-light">
                              <Table size="sm" className="mb-0 bg-transparent">
                                <thead className="text-muted fs-12">
                                  <tr>
                                    <th>Member with no licence</th>
                                    <th>Email</th>
                                    <th>Last login</th>
                                    <th>Last active</th>
                                    <th></th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {(r.unlicensed_list || []).map((u) => (
                                    <tr key={u.User_Id}>
                                      <td>
                                        {u.UserFullName || `User #${u.User_Id}`}
                                        <span className="text-muted fs-12"> #{u.User_Id}</span>
                                      </td>
                                      <td>{u.Email}</td>
                                      <td>{u.LastLoginDateTime ? HandleDateTime(u.LastLoginDateTime) : <span className="text-muted">Never</span>}</td>
                                      <td>{u.LastActiveDateTime ? HandleDateTime(u.LastActiveDateTime) : <span className="text-muted">-</span>}</td>
                                      <td className="text-end">
                                        {u.IsOwner && (
                                          <Badge color="" className="badge-soft-danger me-1">
                                            Owner
                                          </Badge>
                                        )}
                                        {u.UserLockedFlag === 1 && (
                                          <Badge color="" className="badge-soft-secondary">
                                            Locked
                                          </Badge>
                                        )}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </Table>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          )}
        </CardBody>
      </Card>

      <AppModal
        open={Boolean(confirm)}
        onClose={() => !saving && setConfirm(null)}
        title={confirm?.title}
        icon="ri-user-lock-line"
        headerAccent="indigo"
        size="sm"
        onSubmit={save}
        submitLabel="Confirm"
        submitDisabled={saving}
        loading={saving}
        loadingText="Saving..."
      >
        <p className="mb-0">{confirm?.text}</p>
      </AppModal>
    </>
  );
}
